import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format/date";
import { SkeletonRow } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AuditEntry {
  id: string;
  userId?: string;
  entity: string;
  entityId?: string;
  action: string;
  diff?: unknown;
  ip?: string;
  userAgent?: string;
  createdAt: string;
}

interface AuditResponse {
  data: AuditEntry[];
  meta: { total: number; page: number; limit: number };
}

function DiffDialog({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="relative w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold text-sm">
            {entry.entity} / {entry.action}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={16} /></Button>
        </div>
        <pre className="overflow-auto rounded bg-muted p-3 text-xs max-h-96">
          {JSON.stringify(entry.diff, null, 2)}
        </pre>
        <p className="mt-2 text-xs text-muted-foreground">
          {formatDateTime(entry.createdAt)} · IP: {entry.ip}
        </p>
      </div>
    </div>,
    document.body,
  );
}

export function AuditLogPage() {
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const params = { entity: entity || undefined, action: action || undefined, from: from || undefined, to: to || undefined, page };

  // Need both rows + meta.total → bypass apiGet to keep envelope.
  const { data, isLoading } = useQuery<AuditResponse>({
    queryKey: ["audit-log", params],
    queryFn: async () => {
      const res = await apiClient.get<AuditResponse>("/v1/audit-log", { params });
      return res.data;
    },
  });

  const entries = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Nhật ký kiểm toán</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Thực thể</Label>
          <Input placeholder="order, coupon…" value={entity} onChange={(e) => { setEntity(e.target.value); setPage(1); }} className="h-8 w-36 text-sm" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Hành động</Label>
          <Input placeholder="void, adjust…" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="h-8 w-36 text-sm" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Từ ngày</Label>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="h-8 text-sm" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Đến ngày</Label>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="h-8 text-sm" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Thời gian</th>
              <th className="px-4 py-2 text-left font-medium">Thực thể</th>
              <th className="px-4 py-2 text-left font-medium">Hành động</th>
              <th className="px-4 py-2 text-left font-medium">User ID</th>
              <th className="px-4 py-2 text-left font-medium">Tóm tắt</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={5} />)}
            {!isLoading && entries.length === 0 && (
              <tr><td colSpan={5}><EmptyState title="Không có bản ghi" /></td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setSelected(e)}>
                <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                  {formatDateTime(e.createdAt)}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{e.entity}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-[120px]">{e.userId ?? "—"}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-[200px]">
                  {e.diff ? JSON.stringify(e.diff).slice(0, 80) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Tổng: {meta.total} bản ghi</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Trước</Button>
            <span className="flex items-center px-2">Trang {page}</span>
            <Button size="sm" variant="outline" disabled={entries.length < (meta.limit ?? 50)} onClick={() => setPage((p) => p + 1)}>Sau</Button>
          </div>
        </div>
      )}

      {selected && <DiffDialog entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
